import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

const LoginComponent = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("signin");
  const [showResendLink, setShowResendLink] = useState(false);
  const navigate = useNavigate();
  const { user, signIn, signUp, signInWithGoogle, resendVerification } = useAuth();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [navigate, user]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setShowResendLink(false);

    const { error } = await signIn(email, password);

    if (error) {
      if (error.includes("Email not confirmed")) {
        setShowResendLink(true);
        toast.error(
          <div>
            <p>Email not confirmed. Please check your email for verification link.</p>
            <button
              onClick={handleResendVerification}
              className="text-sm underline text-glyco-purple hover:text-glyco-purple-dark mt-1"
            >
              Resend verification email
            </button>
          </div>
        );
      } else {
        toast.error(error);
      }
    } else {
      toast.success("Signed in successfully!");
      navigate("/dashboard");
    }

    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }

    const { error } = await resendVerification(email);

    if (error) {
      toast.error(error);
    } else {
      toast.success("Verification email sent! Please check your inbox.");
      setShowResendLink(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await signUp(email, password, firstName, lastName);

    if (error) {
      toast.error(error);
    } else {
      toast.success("Account created successfully! Please check your email for verification.");
    }

    setIsLoading(false);
  };

  const handleGoogleSignIn = async () => {
    toast.info("Google authentication is not yet supported. Please use email and password to sign in.");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-glyco-purple-light p-4">
      <div className="w-full max-w-md text-center mb-8 animate-fade-in">
        <h1 className="text-3xl font-bold mb-2 text-glyco-purple">Tastermonial Lab</h1>
        <p className="text-slate-600 font-medium">Don't Guess, Just Test</p>
      </div>

      <Card className="w-full max-w-md animate-fade-in">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl text-center">Welcome</CardTitle>
          <CardDescription className="text-center">
            Sign in to your account or create a new one
          </CardDescription>
        </CardHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="signin">
            <form onSubmit={handleSignIn}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                {showResendLink && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      className="text-sm text-glyco-purple hover:underline"
                    >
                      Resend verification email
                    </button>
                  </div>
                )}
              </CardContent>
              <CardFooter className="flex flex-col space-y-4 px-6">
                <Button
                  type="submit"
                  className="w-full bg-glyco-purple hover:bg-glyco-purple-dark"
                  disabled={isLoading}
                  size="sm"
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
                <div className="text-center">
                  <Link to="/forgot-password" className="text-sm text-glyco-purple hover:underline">
                    Forgot password?
                  </Link>
                </div>
              </CardFooter>
            </form>
          </TabsContent>

          <TabsContent value="signup">
            <form onSubmit={handleSignUp}>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4 px-6">
                <Button
                  type="submit"
                  className="w-full bg-glyco-purple hover:bg-glyco-purple-dark"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating account..." : "Create Account"}
                </Button>
              </CardFooter>
            </form>
          </TabsContent>
        </Tabs>

        <div className="px-6 pb-6">
          <div className="relative w-full">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t"></span>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>
          <Button
            variant="outline"
            type="button"
            className="w-full mt-4"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            size="sm"
          >
            Google
          </Button>
        </div>
      </Card>
    </div>
  );
};

export { LoginComponent };